import React from "react";
import { Box, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

// ============================================================================
// Types
// ============================================================================

interface SectionCardProps {
  title: string;
  // Content to display (truncated to maxPreviewLength)
  content?: string;
  // Shown when content is absent
  placeholder?: string;
  maxPreviewLength?: number;
  // 4px left accent border color — use theme palette values or hex
  accentColor?: string;
  // When provided, renders an edit icon button
  onEdit?: () => void;
  // ChartMind-7 passes this so verified decision-rule names can be links.
  // Other callers leave it unset and the section stays plain text.
  renderContent?: (text: string) => React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

const SectionCard: React.FC<SectionCardProps> = ({
  title,
  content = "",
  placeholder = "Not documented",
  maxPreviewLength = 600,
  accentColor,
  onEdit,
  renderContent,
}) => {
  const preview =
    content.length > maxPreviewLength
      ? content.substring(0, maxPreviewLength) + "…"
      : content;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        position: "relative",
        ...(accentColor && { borderLeft: `4px solid ${accentColor}` }),
      }}
    >
      {/* Title row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: accentColor ?? "text.primary",
          }}
        >
          {title}
        </Typography>

        {onEdit && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={onEdit}
              sx={{ color: "text.secondary", ml: 1, flexShrink: 0 }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Content preview */}
      <Typography
        variant="body2"
        sx={{
          color: content ? "text.primary" : "text.disabled",
          whiteSpace: "pre-wrap",
        }}
      >
        {renderContent ? renderContent(content) : (preview || placeholder)}
      </Typography>
    </Paper>
  );
};

export default SectionCard;
